import { Table } from 'antd';
import { useMemo } from 'react';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { SpanEllipsis } from 'views/custom-declaration/declaration-table/components/DeclarationsTable.styled';
import { ColumnsType } from 'antd/es/table';
import { makeTaxKey, buildRowsCalculationsDiffMap } from '../utils';
import { getTaxTypeDescription } from '../calculations';
import { CalculationResult, CalculationSource, CalculationSummary, RowDiffMap, TaxDiffMap } from '../types';

export const diffStyle: React.CSSProperties = { color: 'var(--error-normal)' };

const getTableColumns = (
    taxDiffMap: TaxDiffMap = {},
    rowDiffMap: RowDiffMap = {},
    compareMode: boolean = false
): ColumnsType<CalculationResult> => [
    {
        title: <strong>#</strong>,
        key: 'index',
        render: (_: any, __: any, i: number) => i + 1,
    },
    {
        title: <strong>Details</strong>,
        key: 'details',
        render: (_, record) => (
            <SpanEllipsis>
                Commodity Code: {record.details?.split('\n')[0]?.split(':')[1]?.trim()} <br />
                Country of Origin: {record.details?.split('\n')[1]?.split(':')[1]?.trim()}
            </SpanEllipsis>
        ),
    },
    {
        title: <strong>Tax Type</strong>,
        key: 'taxType',
        render: (_, record) => (
            <div>
                {record?.taxes?.map((tax: any, idx: number) => (
                    <div key={idx}>
                        {tax.taxType} - {getTaxTypeDescription(tax.taxType)}
                    </div>
                ))}
            </div>
        ),
    },
    {
        title: <strong>Tax Base</strong>,
        align: 'right',
        key: 'taxBase',
        render: (_, record) => (
            <div>
                {record?.taxes?.map((tax, idx: number) => {
                    const k = makeTaxKey(record.index, tax.taxType);
                    const isDiff = compareMode && !!taxDiffMap[k]?.taxBase;
                    return (
                        <div key={idx} style={isDiff ? diffStyle : undefined}>
                            {tax?.taxBase?.toFixed(2)}
                        </div>
                    );
                })}
            </div>
        ),
    },
    {
        title: <strong>Tax Rate %</strong>,
        align: 'right',
        key: 'taxRate',
        render: (_, record) => (
            <div>
                {record?.taxes?.map((tax, idx: number) => {
                    const k = makeTaxKey(record.index, tax.taxType);
                    const isDiff = compareMode && !!taxDiffMap[k]?.taxRate;
                    return (
                        <div key={idx} style={isDiff ? diffStyle : undefined}>
                            {tax?.taxRate?.toFixed(2)}
                        </div>
                    );
                })}
            </div>
        ),
    },
    {
        title: <strong>Assessed Amount</strong>,
        align: 'right',
        key: 'assessedAmount',
        render: (_, record) => (
            <div>
                {record.taxes.map((tax, idx) => {
                    const k = makeTaxKey(record.index, tax.taxType);
                    const isDiff = compareMode && !!taxDiffMap[k]?.assessedAmount;
                    return (
                        <div key={idx} style={isDiff ? diffStyle : undefined}>
                            {tax.assessedAmount?.toFixed(2)}
                        </div>
                    );
                })}
            </div>
        ),
    },
    {
        title: <strong>Total Liability</strong>,
        align: 'right',
        dataIndex: 'totalLiability',
        key: 'totalLiability',
        render: (_, record) => {
            const isDiff = compareMode && !!rowDiffMap[String(record.index)]?.totalLiability;
            return (
                <SpanEllipsis style={isDiff ? diffStyle : undefined}>{record.totalLiability?.toFixed(2)}</SpanEllipsis>
            );
        },
    },
];

type Props = {
    target: CalculationSource;
    localData: CalculationSummary;
    revenueData?: CalculationSummary;
};

export default function VatDutyTable({ localData, revenueData, target }: Props) {
    const { taxDiffMap, rowDiffMap } = useMemo(() => {
        if (target === 'revenue' && revenueData) {
            return buildRowsCalculationsDiffMap(localData, revenueData);
        }

        return { taxDiffMap: {}, rowDiffMap: {} };
    }, [target, localData, revenueData]);

    const columns = useMemo(
        () => getTableColumns(taxDiffMap, rowDiffMap, target === 'revenue'),
        [rowDiffMap, target, taxDiffMap]
    );

    return (
        <StyledTableWrapper>
            <Table<CalculationResult>
                pagination={false}
                columns={columns}
                dataSource={target === 'revenue' ? revenueData?.rows : localData?.rows}
                rowKey={(record: CalculationResult) => record.index.toString()}
                onRow={() =>
                    ({
                        'data-testid': 'vat-duty-table-row',
                    } as any)
                }
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
            />
        </StyledTableWrapper>
    );
}
